import type { ElementLayout, PageTheme, SectionType } from "./model";

export type BuiltInTemplateId = "blue_horizon" | "burnished_motion" | "nocturne" | "sleek_monochrome";
export type TemplatePageKey = "cover" | "contents" | "project-details" | "intro" | "concept" | "title" | "references2" | SectionType | "end";
export type PageFamily = "cover" | "index" | "facts" | "split" | "gallery" | "directive" | "timeline" | "list" | "contact" | "closing";

export interface TemplatePageRecipe {
  family: PageFamily;
  theme: PageTheme;
  header: ElementLayout;
  body: ElementLayout;
  image?: ElementLayout;
  imageSlots?: ElementLayout[];
  label: string;
  folio: string;
  imagePosition?: string;
}

const box = (x: number, y: number, width: number, height: number, zIndex = 1): ElementLayout => ({ x, y, width, height, rotation: 0, zIndex });

const LABELS: Record<TemplatePageKey, string> = {
  cover: "Cover",
  contents: "Table of Contents",
  "project-details": "Project Details",
  intro: "About Us",
  concept: "The Brief",
  title: "Title",
  references: "Reference Images",
  references2: "Reference Images",
  casting: "Casting",
  photography: "Photography Brief",
  styling: "Styling / Wardrobe Brief",
  collection: "Collection",
  hair: "Hair Brief",
  makeup: "Makeup Brief",
  production: "Location",
  schedule: "Schedule",
  deliverables: "Deliverables",
  usage: "Usage",
  contact: "Contacts",
  end: "Thank You",
  hair_makeup: "Hair + Makeup Brief",
  team: "Team",
  budget: "Budget",
  notes: "Notes",
  lighting_guide: "Lighting Guide",
  lighting_planner: "Lighting Planner",
  gallery: "Image Page",
  text: "Text Page",
};

const hLight = { bg: "#F8FBFD", text: "#17212B" };
const hWhite = { bg: "#FFFFFF", text: "#17212B" };
const hBlue = { bg: "#DCEEF8", text: "#17212B" };
const hDeep = { bg: "#4A8CCB", text: "#F8FBFD" };
const mPaper = { bg: "#EEE1D2", text: "#211A17" };
const mDark = { bg: "#211A17", text: "#F5EADF" };
const mClay = { bg: "#A94E2F", text: "#FFF7EF" };
const nDark = { bg: "#15110F", text: "#F2E8DD" };
const nBronze = { bg: "#4A3022", text: "#F5E9DC" };
const nPaper = { bg: "#E5D0BB", text: "#211611" };
const sPaper = { bg: "#FAFAF8", text: "#111113" };
const sDark = { bg: "#141416", text: "#F7F7F4" };

const recipe = (
  family: PageFamily,
  theme: PageTheme,
  header: ElementLayout,
  body: ElementLayout,
  image?: ElementLayout,
  imageSlots?: ElementLayout[],
  imagePosition?: string,
): Omit<TemplatePageRecipe, "label" | "folio"> => ({ family, theme, header, body, image, imageSlots, imagePosition });

const horizon: Record<string, Omit<TemplatePageRecipe, "label" | "folio">> = {
  cover: recipe("cover", hLight, box(7.4, 23, 47, 45, 5), box(7.4, 85, 85.2, 10, 5), box(56, 10, 37, 71), undefined, "52% 42%"),
  contents: recipe("index", hLight, box(49, 9, 43, 17, 5), box(49, 31, 43, 60, 5), box(0, 0, 40, 100), undefined, "45% 42%"),
  "project-details": recipe("facts", hLight, box(49.5, 18, 42, 19, 5), box(49.5, 49, 42, 35, 5), box(0, 0, 42, 100), undefined, "50% 35%"),
  intro: recipe("split", hLight, box(7, 12, 37, 18, 5), box(7, 36, 38, 48, 5), box(57, 7, 36, 78), undefined, "52% 42%"),
  concept: recipe("directive", hLight, box(7.4, 21, 47, 28, 3), box(7.4, 52, 36, 24, 3), box(58, 17, 32, 53.5), undefined, "50% 35%"),
  collection: recipe("gallery", hLight, box(6, 4, 42, 10, 8), box(6, 89.5, 52, 8, 8), undefined, [
    box(6, 19, 30, 68),
    box(40, 19, 12.4, 21.6), box(53.9, 19, 12.4, 21.6), box(67.8, 19, 12.4, 21.6), box(81.7, 19, 12.4, 21.6),
    box(40, 42.2, 12.4, 21.6), box(53.9, 42.2, 12.4, 21.6), box(67.8, 42.2, 12.4, 21.6), box(81.7, 42.2, 12.4, 21.6),
    box(40, 65.4, 12.4, 21.6), box(53.9, 65.4, 12.4, 21.6), box(67.8, 65.4, 12.4, 21.6), box(81.7, 65.4, 12.4, 21.6),
  ]),
  title: recipe("cover", hLight, box(5, 32, 64, 27, 6), box(66, 24, 27, 8, 6), box(38, 0, 39, 100), undefined, "50% 38%"),
  references: recipe("gallery", hLight, box(6, 5, 42, 8, 8), box(6, 89.5, 52, 8, 8), undefined, [box(6, 28.5, 34.5, 58), box(55, 27, 39, 22), box(55, 52.5, 18.5, 34), box(76.5, 52.5, 18, 34)]),
  references2: recipe("gallery", hLight, box(56, 3.5, 38, 6, 8), box(6, 3.5, 30, 6, 8), undefined, [box(2.8, 9.4, 29, 24.4), box(2.8, 34.8, 29, 62.4), box(32.4, 9.4, 28.8, 48.5), box(32.4, 58.7, 28.8, 38.5), box(61.7, 9.4, 35.9, 87.8)]),
  casting: recipe("split", hLight, box(6, 8, 35, 22, 6), box(6, 38, 37, 48, 6), box(52, 0, 48, 100), undefined, "52% 34%"),
  photography: recipe("split", hLight, box(61, 10, 33, 22, 6), box(61, 36, 33, 52, 6), box(0, 0, 54, 100), undefined, "46% 45%"),
  styling: recipe("gallery", hWhite, box(6, 7, 39, 19, 6), box(6, 33, 34, 18, 6), undefined, [box(0, 59, 36, 41), box(48, 10, 21, 38), box(72, 10, 22, 38), box(48, 54, 21, 39), box(72, 54, 22, 39)]),
  hair: recipe("directive", hLight, box(6, 7, 38, 17, 6), box(6, 29, 35, 22, 6), undefined, [box(49, 0, 51, 58), box(0, 58, 49, 42)]),
  makeup: recipe("directive", hWhite, box(56, 7, 38, 17, 6), box(59, 29, 35, 22, 6), undefined, [box(0, 0, 51, 58), box(51, 58, 49, 42)]),
  production: recipe("split", hLight, box(61, 12, 33, 22, 6), box(61, 38, 33, 47, 6), box(0, 0, 54, 100), undefined, "42% 55%"),
  schedule: recipe("timeline", hWhite, box(6, 8, 31, 20, 5), box(39, 15, 54, 70, 5)),
  deliverables: recipe("list", hLight, box(6, 9, 35, 21, 5), box(39, 34, 54, 51, 5)),
  usage: recipe("directive", hWhite, box(6, 8, 31, 20, 5), box(42, 13, 51, 72, 5)),
  contact: recipe("contact", hLight, box(6, 9, 31, 20, 5), box(43, 14, 50, 68, 5)),
  end: recipe("closing", hWhite, box(12, 29, 76, 25, 5), box(24, 62, 52, 12, 5)),
};

const motion: Record<string, Omit<TemplatePageRecipe, "label" | "folio">> = {
  cover: recipe("cover", mDark, box(6, 61, 74, 29, 5), box(6, 8, 50, 8, 5), box(0, 0, 100, 100), undefined, "49% 42%"),
  contents: recipe("index", mPaper, box(6, 8, 38, 22, 5), box(51, 10, 42, 78, 4), box(0, 0, 43, 100), undefined, "40% 50%"),
  "project-details": recipe("facts", mClay, box(7, 9, 40, 24, 5), box(53, 14, 39, 67, 5)),
  intro: recipe("split", mDark, box(53, 9, 39, 22, 5), box(53, 40, 38, 46, 5), box(0, 0, 47, 100), undefined, "50% 48%"),
  concept: recipe("directive", mClay, box(7, 8, 42, 24, 5), box(7, 55, 46, 34, 5), box(58, 35, 35, 55), undefined, "48% 46%"),
  title: recipe("cover", mDark, box(4, 40, 60, 24, 6), box(63, 34, 33, 7, 6), box(0, 0, 100, 100), undefined, "52% 36%"),
  references: recipe("gallery", mDark, box(5, 6, 40, 24, 8), box(0, 0, 100, 100), undefined, [box(0, 0, 54, 100), box(55, 0, 22, 46), box(78, 0, 22, 46), box(55, 47, 45, 53), box(7, 62, 18, 26, 4), box(28, 70, 18, 23, 4)]),
  references2: recipe("gallery", mDark, box(3.7, 82, 44, 12, 8), box(0, 0, 100, 100), undefined, [box(3.7, 12.5, 36.6, 25.7), box(3.7, 39, 36.6, 41.6), box(41.1, 12.5, 22.9, 38.3), box(41.1, 51.6, 22.9, 29), box(64.8, 12.5, 30.5, 32.2), box(64.8, 45.5, 30.5, 35.1)]),
  casting: recipe("split", mDark, box(6, 61, 46, 28, 6), box(0, 0, 100, 100), box(0, 0, 100, 100), undefined, "46% 36%"),
  photography: recipe("split", mDark, box(52, 8, 41, 30, 6), box(0, 0, 100, 100), box(0, 0, 100, 100), undefined, "52% 46%"),
  styling: recipe("split", mDark, box(7, 61, 46, 28, 6), box(0, 0, 100, 100), box(0, 0, 100, 100), undefined, "58% 50%"),
  hair: recipe("directive", mPaper, box(7, 9, 39, 24, 4), box(48, 42, 44, 42, 4)),
  makeup: recipe("directive", mClay, box(52, 10, 40, 25, 4), box(8, 47, 46, 36, 4)),
  production: recipe("split", mDark, box(53, 60, 39, 27, 6), box(0, 0, 100, 100), box(0, 0, 100, 100), undefined, "42% 54%"),
  schedule: recipe("timeline", mDark, box(7, 8, 39, 23, 4), box(39, 37, 54, 50, 4)),
  deliverables: recipe("list", mClay, box(7, 9, 43, 24, 4), box(42, 42, 50, 43, 4)),
  usage: recipe("directive", mDark, box(7, 9, 38, 24, 4), box(51, 16, 41, 66, 4)),
  contact: recipe("contact", mPaper, box(7, 9, 39, 24, 4), box(47, 17, 45, 62, 4)),
  end: recipe("closing", mDark, box(8, 34, 84, 28, 5), box(22, 66, 56, 10, 5)),
};

const nocturne: Record<string, Omit<TemplatePageRecipe, "label" | "folio">> = {
  cover: recipe("cover", nDark, box(7, 62, 60, 26, 5), box(72, 81, 20, 7, 5), box(0, 0, 100, 100), undefined, "51% 45%"),
  contents: recipe("index", nDark, box(7, 8, 38, 22, 5), box(52, 12, 40, 76, 5)),
  "project-details": recipe("facts", nPaper, box(7, 9, 34, 22, 4), box(52, 13, 40, 69, 4)),
  intro: recipe("split", nDark, box(8, 9, 43, 22, 5), box(8, 40, 43, 45, 5), box(58, 9, 34, 82), undefined, "50% 47%"),
  concept: recipe("directive", nBronze, box(55, 8, 38, 24, 5), box(55, 44, 38, 38, 5), box(7, 38, 40, 49), undefined, "48% 50%"),
  title: recipe("cover", nDark, box(4, 40, 60, 24, 6), box(63, 34, 33, 7, 6), box(0, 0, 100, 100), undefined, "52% 38%"),
  references: recipe("gallery", nDark, box(7, 68, 38, 22, 8), box(0, 0, 100, 100), undefined, [box(0, 0, 48, 100), box(49, 0, 25, 48), box(75, 0, 25, 48), box(49, 49, 33, 51), box(83, 49, 17, 51)]),
  references2: recipe("gallery", nDark, box(3.7, 82, 44, 12, 8), box(0, 0, 100, 100), undefined, [box(3.7, 12.5, 36.6, 25.7), box(3.7, 39, 36.6, 41.6), box(41.1, 12.5, 22.9, 38.3), box(41.1, 51.6, 22.9, 29), box(64.8, 12.5, 30.5, 32.2), box(64.8, 45.5, 30.5, 35.1)]),
  casting: recipe("split", nDark, box(58, 58, 34, 29, 6), box(5, 5, 48, 90), box(0, 0, 100, 100), undefined, "50% 36%"),
  photography: recipe("split", nDark, box(7, 9, 35, 28, 6), box(48, 5, 47, 90), box(0, 0, 100, 100), undefined, "46% 44%"),
  styling: recipe("split", nDark, box(58, 58, 34, 29, 6), box(5, 5, 48, 90), box(0, 0, 100, 100), undefined, "58% 50%"),
  hair: recipe("directive", nBronze, box(7, 9, 37, 24, 4), box(52, 17, 40, 64, 4)),
  makeup: recipe("directive", nDark, box(56, 9, 36, 24, 4), box(8, 43, 48, 39, 4)),
  production: recipe("split", nDark, box(58, 59, 34, 28, 6), box(5, 5, 47, 90), box(0, 0, 100, 100), undefined, "42% 54%"),
  schedule: recipe("timeline", nPaper, box(7, 9, 35, 22, 4), box(34, 38, 58, 48, 4)),
  deliverables: recipe("list", nDark, box(7, 9, 39, 23, 4), box(44, 43, 48, 42, 4)),
  usage: recipe("directive", nBronze, box(7, 9, 38, 23, 4), box(52, 16, 40, 65, 4)),
  contact: recipe("contact", nPaper, box(7, 9, 36, 23, 4), box(50, 16, 42, 64, 4)),
  end: recipe("closing", nDark, box(16, 33, 68, 26, 5), box(24, 64, 52, 11, 5)),
};

const monochrome: Record<string, Omit<TemplatePageRecipe, "label" | "folio">> = {
  cover: recipe("cover", sPaper, box(5, 5, 90, 12, 5), box(5, 92.5, 90, 4.5, 5), box(5, 20, 90, 70), undefined, "50% 42%"),
  contents: recipe("index", sPaper, box(6, 7, 30, 22, 5), box(43, 9, 51, 78, 5)),
  "project-details": recipe("facts", sPaper, box(6, 7, 31, 24, 5), box(50, 11, 44, 72, 5)),
  intro: recipe("split", sDark, box(6, 9, 39, 22, 5), box(6, 39, 39, 49, 5), box(51, 9, 43, 80), undefined, "50% 48%"),
  concept: recipe("directive", sPaper, box(5, 23, 31, 17, 5), box(5, 51, 31, 23, 5), box(42, 11, 53, 47), undefined, "47% 50%"),
  title: recipe("cover", sPaper, box(3, 40, 56, 22, 6), box(67, 33, 29, 7, 6), box(31, 0, 43, 100), undefined, "50% 36%"),
  references: recipe("gallery", sPaper, box(46, 7, 49, 23, 8), box(0, 0, 100, 100), undefined, [box(0, 58, 24.25, 42), box(25.25, 58, 24.25, 42), box(50.5, 58, 24.25, 42), box(75.75, 58, 24.25, 42)]),
  references2: recipe("gallery", sPaper, box(3.7, 82, 44, 12, 8), box(0, 0, 100, 100), undefined, [box(3.7, 12.5, 36.6, 25.7), box(3.7, 39, 36.6, 41.6), box(41.1, 12.5, 22.9, 38.3), box(41.1, 51.6, 22.9, 29), box(64.8, 12.5, 30.5, 32.2), box(64.8, 45.5, 30.5, 35.1)]),
  casting: recipe("split", sPaper, box(6, 8, 30, 30, 6), box(41, 0, 59, 100), box(0, 0, 100, 100), undefined, "51% 34%"),
  photography: recipe("split", sPaper, box(64, 53, 31, 37, 6), box(0, 0, 57, 100), box(0, 0, 100, 100), undefined, "46% 45%"),
  styling: recipe("directive", sPaper, box(4, 9, 46, 12, 6), box(5, 26, 36, 28, 6), undefined, [box(0, 56, 36, 44), box(51, 12, 24.5, 43), box(76, 12, 24, 43), box(51, 56, 24.5, 44), box(76, 56, 24, 44)]),
  collection: recipe("gallery", sPaper, box(4, 2.5, 18, 5, 6), box(4, 88, 39, 8, 6), undefined, [
    box(4, 9, 10.8, 24.7), box(15.7, 9, 10.8, 24.7), box(27.4, 9, 10.8, 24.7), box(39.1, 9, 10.8, 24.7),
    box(4, 34.7, 10.8, 24.7), box(15.7, 34.7, 10.8, 24.7), box(27.4, 34.7, 10.8, 24.7), box(39.1, 34.7, 10.8, 24.7),
    box(4, 60.4, 10.8, 24.7), box(15.7, 60.4, 10.8, 24.7), box(27.4, 60.4, 10.8, 24.7), box(39.1, 60.4, 10.8, 24.7),
    box(54, 9, 32, 76.1),
  ]),
  hair: recipe("directive", sPaper, box(4, 5, 42, 15, 5), box(7, 24, 37, 18, 5), undefined, [box(50, 0, 50, 50), box(0, 50, 50, 50)]),
  makeup: recipe("directive", sPaper, box(4, 5, 42, 15, 5), box(7, 24, 37, 18, 5), undefined, [box(50, 0, 50, 50), box(0, 50, 50, 50)]),
  hair_makeup: recipe("directive", sPaper, box(4, 5, 42, 15, 5), box(7, 24, 37, 18, 5), undefined, [box(50, 0, 50, 50), box(0, 50, 50, 50)]),
  production: recipe("split", sPaper, box(64, 15, 31, 72, 6), box(0, 0, 57, 100), box(0, 0, 100, 100), undefined, "41% 55%"),
  schedule: recipe("timeline", sDark, box(6, 8, 31, 24, 4), box(41, 13, 53, 71, 4)),
  deliverables: recipe("list", sPaper, box(6, 8, 44, 25, 4), box(6, 42, 70, 43, 4)),
  usage: recipe("directive", sPaper, box(6, 8, 31, 24, 4), box(43, 13, 51, 70, 4)),
  contact: recipe("contact", sDark, box(6, 8, 31, 24, 4), box(42, 15, 52, 65, 4)),
  end: recipe("closing", sDark, box(8, 34, 84, 26, 5), box(25, 65, 50, 10, 5)),
};

const SYSTEMS: Record<BuiltInTemplateId, Record<string, Omit<TemplatePageRecipe, "label" | "folio">>> = {
  blue_horizon: horizon,
  burnished_motion: motion,
  nocturne,
  sleek_monochrome: monochrome,
};

export const PHOTOGRAPHY_TEMPLATE_PAGES: TemplatePageKey[] = [
  "cover", "contents", "project-details", "intro", "concept", "title", "references", "casting", "photography", "styling", "collection", "hair", "makeup", "production", "schedule", "deliverables", "usage", "contact", "end",
];

export function templatePageRecipe(templateId: string | undefined, pageKey: TemplatePageKey): TemplatePageRecipe | undefined {
  if (!templateId || !(templateId in SYSTEMS)) return undefined;
  const system = SYSTEMS[templateId as BuiltInTemplateId];
  const value = system[pageKey];
  if (!value) return undefined;
  const index = PHOTOGRAPHY_TEMPLATE_PAGES.indexOf(pageKey);
  return { ...value, label: LABELS[pageKey], folio: String(Math.max(1, index + 1)).padStart(2, "0") };
}

export function templatePageTheme(templateId: string | undefined, pageKey: TemplatePageKey): PageTheme {
  return templatePageRecipe(templateId, pageKey)?.theme || {};
}
